import React from 'react';
import { parsePhoneNumberFromString } from 'libphonenumber-js/max';
import { Close, Done, DoneAll, QueryBuilder } from '@material-ui/icons';
import { Button, SvgIcon } from '@material-ui/core';
import {
  ChannelTypeEnum,
  MessageStatusEnum,
  MessageTypeEnum,
} from '~/enums/conversations';
import { Message } from '~/interfaces/conversations';
import { dayjs, ReactSVG, useTranslation } from '~/dependencies';
import { log } from '~/helpers';
import { getChannelIconById } from '~/helpers/conversation';
import FileThumbs from '../FileThumbs';
import {
  Container,
  Body,
  Avatar,
  Footer,
  MessageComponent,
  Scheduled,
} from './styles';
import MessageText from '../MessageText';
import ContactPreview from '../ContactPreview';

// Returns e.g., "9:21 PM"
function formatTimestamp(date: string, userTz: string) {
  return dayjs(date)
    .tz(userTz)
    .format('h:mm A');
}

function composeMessageInfo(
  timestamp: string,
  senderAgentDisplayName: string | undefined,
  senderId: string | null | undefined = null
) {
  const parts = [timestamp];
  if (senderAgentDisplayName) parts.push(senderAgentDisplayName);
  if (senderId) parts.push(senderId);
  return parts.join(' • ');
}

type StatusIconConfig = { Icon: typeof SvgIcon; className: string };
type ChannelStatusIconMap = Partial<
  Record<MessageStatusEnum, StatusIconConfig>
>;

const baseStatusIcons: ChannelStatusIconMap = {
  [MessageStatusEnum.SENT]: { Icon: Done, className: 'icon' },
  [MessageStatusEnum.DELIVERED]: { Icon: DoneAll, className: 'icon delivered' },
  [MessageStatusEnum.FAILED]: { Icon: Close, className: 'icon failed' },
  [MessageStatusEnum.SCHEDULED]: { Icon: QueryBuilder, className: 'icon' },
};

const statusIconsByChannel: Record<ChannelTypeEnum, ChannelStatusIconMap> = {
  [ChannelTypeEnum.SMS]: baseStatusIcons,
  [ChannelTypeEnum.INTERNAL]: baseStatusIcons,
  [ChannelTypeEnum.WHATSAPP]: {
    ...baseStatusIcons,
    [MessageStatusEnum.READ]: { Icon: DoneAll, className: 'icon read' },
  },
};

const getMessageStatusIcon = (
  status: number | null,
  channelType: ChannelTypeEnum
) => {
  if (status === null) return null;
  const config =
    statusIconsByChannel[channelType]?.[status as MessageStatusEnum];
  if (!config) return null;
  const { Icon, className } = config;
  return <Icon className={className} />;
};

const RECEIVED_CODE = 1;
const SENT_CODE = 2;

type RowProps = {
  item: Message;
  userTz: string;
  searchValue: string;
  cancelMessage: (data: any) => void;
  /**
   * @deprecated This prop is no longer used and will be removed in a future release.
   * The channel type is now determined from the message data.
   */
  // channelType: ChannelTypeEnum;
};

function Row({ item, userTz, searchValue, cancelMessage }: RowProps) {
  // HOOKS
  const { t } = useTranslation();

  // CONST's
  const {
    id,
    conversationId,
    direction,
    createdAt,
    message,
    senderAgentDisplayName,
    statuses,
    scheduledFor,
    deliveredAt,
    senderId,
    contentUrl,
    messageType,
    metadata,
    channelType,
    thumbnailUrl,
  } = item;

  const fileType: string | undefined =
    metadata?.media?.[0]?.contentType || metadata?.media?.mimeType;
  // WA2-7389: Only outbound media has names and sizes
  const fileName: string | undefined = metadata?.media?.filename;
  const fileSize: number | undefined = metadata?.media?.fileSize;

  const isSentCode = direction === SENT_CODE;
  const messageStatus =
    statuses && statuses.length > 0 ? statuses[0].status : -1;

  const hasPossibleTextMessage = messageType !== MessageTypeEnum.FILE;
  const isSticker = messageType === MessageTypeEnum.STICKER;
  // A vCard (Virtual Contact File, commonly with a .vcf extension)
  const isContactCard = messageType === MessageTypeEnum.CONTACT;
  const isMediaMessage =
    messageType === MessageTypeEnum.FILE ||
    messageType === MessageTypeEnum.IMAGE ||
    messageType === MessageTypeEnum.STICKER ||
    messageType === MessageTypeEnum.VIDEO ||
    messageType === MessageTypeEnum.AUDIO;

  const renderMessageDateTime = () => {
    if (scheduledFor && deliveredAt === null) {
      const scheduledDateTime = composeMessageInfo(
        `${t('screens.conversation.messages.scheduledTo')}: ${dayjs(
          scheduledFor
        )
          .tz(userTz)
          .format('M/D/YYYY h:mm:ss A')}`,
        senderAgentDisplayName
      );

      return (
        <Scheduled>
          <span>{scheduledDateTime}</span>
          <Button
            color="primary"
            size="small"
            disableRipple
            disableElevation
            onClick={() => cancelMessage({ conversationId, messageId: id })}>
            {t('components.sendSMS.cancel')}
          </Button>
        </Scheduled>
      );
    }

    return composeMessageInfo(
      formatTimestamp(createdAt, userTz),
      direction === SENT_CODE ? senderAgentDisplayName : undefined,
      parsePhoneNumberFromString(String(senderId))?.formatInternational() ||
        senderId
    );
  };

  /**
   * FIXME:
   * This is a workaround to display file name correctly.
   * The backend currently doesn't return the file name as property of the file object,
   * but instead includes it in the message text.
   * Once the backend is fixed, this can be removed and the file name can be taken directly from the file object.
   */
  function getFileName() {
    return fileName || message || 'Unknown File Format';
  }

  function renderContactPreview() {
    // const contactName = metadata?.rawPayload?.profileName || 'Unknown Contact';
    // const contactNumber = metadata?.rawPayload?.waId || 'Unknown Number';
    return <ContactPreview url={contentUrl!} />;
  }

  return (
    <Container
      data-test-id="message-row"
      onDoubleClick={() => log.debug('Message Info: ', item)}>
      {direction === SENT_CODE && (
        <Avatar marginRight style={{ opacity: 0 }}>
          <span>-</span>
        </Avatar>
      )}
      <MessageComponent
        data-test-id="message-bubble"
        isSender={isSentCode}
        isScheduled={scheduledFor && deliveredAt === null}
        {...(isSticker && { width: '170px' })}>
        <Body>
          {isMediaMessage ? (
            <FileThumbs
              file={{
                name: getFileName(),
                type: messageType,
                size: fileSize || 0,
                url: contentUrl,
                thumbnailUrl,
                contentType: fileType,
              }}
            />
          ) : null}

          {isContactCard && renderContactPreview()}

          {hasPossibleTextMessage && (
            <MessageText text={message} searchValue={searchValue} />
          )}
        </Body>
        <Footer data-test-id="message-footer" isSender={isSentCode}>
          <ReactSVG
            className="channel-icon"
            src={getChannelIconById(channelType)}
          />
          {renderMessageDateTime()}
          {isSentCode ? getMessageStatusIcon(messageStatus, channelType) : null}
        </Footer>
      </MessageComponent>
      {direction === RECEIVED_CODE && (
        <Avatar marginLeft style={{ opacity: 0 }}>
          <span>-</span>
        </Avatar>
      )}
    </Container>
  );
}

export default React.memo(Row);
